import {useCallback, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {useCategory} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui'

import {useOptimizelyPage} from '../../../hooks/use-optimizely-page'
import useQuickViewCart from './use-quick-view-cart'
import useQuickViewNotifyMe from './use-quick-view-notify-me'
import useQuickViewProductData from './use-quick-view-product-data'

// MRT multi site
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../../utils/site-config'

const useQuickView = ({
    productId,
    isProductPartOfSet,
    isProductPartOfBundle,
    setIsQuickViewOpen,
    isNotifyMeModalStateOpen,
    setColorValue,
    setProductId,
    isWishlistPage,
    wishlistProductId,
    searchHitData,
    onVariantSelect,
    onProductChange,
    onLocalSelectionsChange,
    onQuickViewClose,
    forceMobileLayout = false
}) => {
    const intl = useIntl()
    const breakpointIsDesktop = useBreakpointValue({base: false, lg: true})
    const isDesktop = forceMobileLayout ? false : breakpointIsDesktop
    const config = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const notifyMeEnabled = getSiteEnvVarValue(config.app, 'NOTIFY_ME_ENABLED', siteId) === 'true'
    const {data: optimizelyData} = useOptimizelyPage('pdp')

    const {
        product,
        variant,
        quantity,
        variationParams,
        variationAttributes,
        priceData,
        showSkeleton,
        showOptionsMessage,
        setShowOptionsMessage,
        missingAttributeMessages,
        handleSwatchChange,
        getInitialSlideIndex,
        getAttributeLabel,
        hasUnselectedVariations,
        disableAddToCart,
        cartInventoryMessage,
        isVariationDataLoading,
        isWishlistEnabled,
        resetSelections
    } = useQuickViewProductData({
        productId,
        isProductPartOfSet,
        isProductPartOfBundle,
        setColorValue,
        setProductId,
        searchHitData,
        onVariantSelect,
        onProductChange,
        onLocalSelectionsChange,
        intl
    })

    const {
        isNotifyMe,
        isFewItemsLeft,
        isNotifyMeLoading,
        notifyMeError,
        isNotifyMeModalOpen,
        notifyMeSuccessContent,
        handleNotifyMe,
        handleNotifyMeCloseModal,
        notifyMeIcon
    } = useQuickViewNotifyMe({
        product,
        variant,
        variationAttributes,
        variationParams,
        notifyMeEnabled,
        isVariationDataLoading,
        isNotifyMeModalStateOpen
    })

    const {data: category} = useCategory({
        parameters: {
            id: product?.primaryCategoryId,
            levels: 1
        }
    })

    const [primaryCategory, setPrimaryCategory] = useState(category)
    const [optionsAttempt, setOptionsAttempt] = useState(0)

    useEffect(() => {
        if (category) {
            setPrimaryCategory(category)
        }
    }, [category])

    const handleWrapperClick = useCallback((event) => {
        const outerAnchor = event.currentTarget.closest('a')
        const innerAnchor = event.target.closest('a')

        // Allow nested anchors to keep their navigation behavior
        if (innerAnchor && outerAnchor && innerAnchor !== outerAnchor) {
            return
        }

        if (outerAnchor) {
            event.preventDefault()
            event.stopPropagation()
        }
    }, [])

    const {handleCartItem, addToCartError} = useQuickViewCart({
        product,
        variant,
        quantity,
        isDesktopViewport: breakpointIsDesktop,
        isWishlistPage,
        wishlistProductId,
        setIsQuickViewOpen,
        primaryCategory,
        hasUnselectedVariations,
        setShowOptionsMessage,
        onMissingVariations: () => setOptionsAttempt((count) => count + 1),
        intl,
        onAddToCartSuccess: resetSelections,
        onQuickViewClose
    })

    return {
        isDesktop,
        product,
        variationParams,
        variationAttributes,
        variant,
        priceData,
        showSkeleton,
        showOptionsMessage,
        missingAttributeMessages,
        getAttributeLabel,
        isNotifyMe,
        isFewItemsLeft,
        isNotifyMeLoading,
        notifyMeError,
        isNotifyMeModalOpen,
        notifyMeSuccessContent,
        disableAddToCart: disableAddToCart || !!addToCartError,
        cartInventoryMessage: cartInventoryMessage || addToCartError,
        handleNotifyMe,
        handleNotifyMeCloseModal,
        handleCartItem,
        handleSwatchChange,
        getInitialSlideIndex,
        notifyMeIcon,
        optimizelyData,
        primaryCategory,
        isWishlistEnabled,
        handleWrapperClick,
        optionsAttempt,
        triggerOptionsAttempt: () => setOptionsAttempt((count) => count + 1),
        hasUnselectedVariations,
        setShowOptionsMessage,
        quantity,
        isVariationDataLoading
    }
}

export default useQuickView
